import React, { useEffect, useMemo, useState } from 'react';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const toISODate = (year, monthIndex, day) => {
  const m = String(monthIndex + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
};

const parseISODate = (iso) => {
  const raw = String(iso || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  return { year: y, monthIndex: m - 1, day: d };
};

/**
 * Compact month calendar for Copy Employees.
 * Dates with a saved crew roster show a blue dot and are selectable.
 */
export default function CrewCopyFromDateCalendar({
  selectedDate = '',
  onSelect,
  availableDates = [],
  excludeDate = '',
  disabled = false,
  className = '',
  countNoun = 'employee',
  legend = 'Saved crew roster — click to copy',
}) {
  const availableMap = useMemo(() => {
    const map = new Map();
    (availableDates || []).forEach((d) => {
      const key = String(d?.workDate || '').trim();
      if (!key) return;
      map.set(key, Number(d.equipmentCount ?? d.employeeCount) || 0);
    });
    return map;
  }, [availableDates]);

  const initial = useMemo(() => {
    const preferred =
      parseISODate(selectedDate) ||
      parseISODate(availableDates?.[0]?.workDate) ||
      parseISODate(excludeDate);
    if (preferred) return { year: preferred.year, monthIndex: preferred.monthIndex };
    const now = new Date();
    return { year: now.getFullYear(), monthIndex: now.getMonth() };
  }, [selectedDate, availableDates, excludeDate]);

  const [viewYear, setViewYear] = useState(initial.year);
  const [viewMonth, setViewMonth] = useState(initial.monthIndex);

  useEffect(() => {
    setViewYear(initial.year);
    setViewMonth(initial.monthIndex);
  }, [initial.year, initial.monthIndex]);

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

    const result = [];
    for (let i = 0; i < 42; i += 1) {
      let year = viewYear;
      let monthIndex = viewMonth;
      let day;
      let inCurrentMonth = true;

      if (i < startOffset) {
        inCurrentMonth = false;
        monthIndex = viewMonth - 1;
        if (monthIndex < 0) {
          monthIndex = 11;
          year = viewYear - 1;
        }
        day = daysInPrev - startOffset + i + 1;
      } else if (i >= startOffset + daysInMonth) {
        inCurrentMonth = false;
        monthIndex = viewMonth + 1;
        if (monthIndex > 11) {
          monthIndex = 0;
          year = viewYear + 1;
        }
        day = i - startOffset - daysInMonth + 1;
      } else {
        day = i - startOffset + 1;
      }

      const iso = toISODate(year, monthIndex, day);
      const employeeCount = availableMap.get(iso) || 0;
      const hasData = employeeCount > 0;
      const isExcluded = Boolean(excludeDate) && iso === excludeDate;
      const selectable = !disabled && hasData && !isExcluded;

      result.push({
        key: `${iso}-${i}`,
        iso,
        day,
        inCurrentMonth,
        hasData,
        selectable,
        employeeCount,
        isSelected: Boolean(selectedDate) && selectedDate === iso,
      });
    }
    return result;
  }, [viewYear, viewMonth, availableMap, excludeDate, selectedDate, disabled]);

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const years = useMemo(() => {
    const list = [];
    for (let y = viewYear - 5; y <= viewYear + 5; y += 1) list.push(y);
    return list;
  }, [viewYear]);

  return (
    <div className={`w-full select-none ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <button
          type="button"
          onClick={goPrevMonth}
          disabled={disabled}
          className="h-8 w-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="flex items-center gap-1.5 min-w-0">
          <select
            value={viewMonth}
            disabled={disabled}
            onChange={(e) => setViewMonth(Number(e.target.value))}
            className="h-8 rounded-md border border-gray-300 bg-white px-1.5 text-sm text-[#2C2C2C]"
            aria-label="Month"
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={name} value={idx}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={viewYear}
            disabled={disabled}
            onChange={(e) => setViewYear(Number(e.target.value))}
            className="h-8 rounded-md border border-gray-300 bg-white px-1.5 text-sm text-[#2C2C2C]"
            aria-label="Year"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={goNextMonth}
          disabled={disabled}
          className="h-8 w-8 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 text-center mb-0.5">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-[11px] font-medium text-gray-500 py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 text-center">
        {cells.map((cell) => {
          const baseText = cell.inCurrentMonth ? 'text-[#2C2C2C]' : 'text-gray-300';
          const selectedCls = cell.isSelected
            ? 'bg-blue-50 text-blue-700 font-semibold ring-1 ring-blue-500'
            : cell.selectable
              ? 'hover:bg-gray-50'
              : '';

          return (
            <button
              key={cell.key}
              type="button"
              disabled={!cell.selectable}
              onClick={() => {
                if (cell.selectable) onSelect?.(cell.iso);
              }}
              title={
                cell.hasData
                  ? `${cell.employeeCount} ${countNoun}${
                      cell.employeeCount === 1 ? '' : 's'
                    } saved`
                  : undefined
              }
              className={`relative mx-auto flex h-9 w-9 flex-col items-center justify-center rounded-md text-sm ${baseText} ${selectedCls} ${
                cell.selectable ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <span>{cell.day}</span>
              {cell.hasData && (
                <span
                  className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${
                    cell.isSelected ? 'bg-blue-600' : 'bg-blue-500'
                  }`}
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
        <span>{legend}</span>
      </div>
    </div>
  );
}
